import React, { useEffect, useState } from "react";
import { api, statusBadge, categoryColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, User, Plus, X, CalendarDays } from "lucide-react";

const manage = (r) => ["admin", "manager"].includes(r);
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

const mondayOf = (d) => {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Compose the location line shown under a ticket title in Schedule cards.
// Priority: address (street) + unit label. Falls back to property/unit name for legacy tickets.
const locLine = (tk) => {
  const parts = [];
  const address = (tk?.address || "").trim();
  if (address) parts.push(address);
  // unit_name is stored as "<property> — <unit label>"; extract only the unit label to avoid duplicating the address
  const uname = tk?.unit_name || "";
  const unitLabel = uname.includes(" — ") ? uname.split(" — ").slice(1).join(" — ").trim() : uname.trim();
  if (unitLabel && !parts.join(" ").toLowerCase().includes(unitLabel.toLowerCase())) parts.push(unitLabel);
  if (parts.length) return parts.join(" · ");
  return tk?.property_name || tk?.unit_name || "";
};

export default function Schedule() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [tickets, setTickets] = useState([]);
  const [techs, setTechs] = useState([]);
  const [available, setAvailable] = useState([]);
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [techFilter, setTechFilter] = useState("");
  const [panel, setPanel] = useState(null); // { ticket, date, start, duration, assigned_to }

  const load = () => {
    api.get("/tickets").then(setTickets);
    api.get("/tickets/available").then(setAvailable).catch(() => {});
  };
  useEffect(() => {
    load();
    if (manage(user.role)) api.get("/technicians").then(setTechs).catch(() => {});
  }, []); // eslint-disable-line

  const claim = async (tk) => {
    try {
      await api.post(`/tickets/${tk.id}/claim`, { scheduled_date: new Date().toISOString(), scheduled_end: new Date(Date.now() + 2 * 3600000).toISOString() });
      toast.success(t("claimed"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 7);

  const visible = tickets.filter((tk) => !techFilter || tk.assigned_to === techFilter);
  const scheduled = visible.filter((tk) => tk.scheduled_date);
  const unscheduled = visible.filter((tk) => !tk.scheduled_date && !["completed", "cancelled", "completed_no_charge"].includes(tk.status));

  const dayTickets = (d) => {
    const key = ymd(d);
    return scheduled
      .filter((tk) => tk.scheduled_date.slice(0, 10) === key)
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "";

  const openPanel = (tk) => {
    const start = tk.scheduled_date ? new Date(tk.scheduled_date) : new Date(new Date().setHours(9, 0, 0, 0));
    let duration = 2;
    if (tk.scheduled_date && tk.scheduled_end) {
      duration = Math.max(0.5, Math.round(((new Date(tk.scheduled_end) - new Date(tk.scheduled_date)) / 3600000) * 2) / 2);
    }
    setPanel({
      ticket: tk,
      date: ymd(start),
      start: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
      duration,
      assigned_to: tk.assigned_to || "",
    });
  };

  const savePanel = async () => {
    const p = panel;
    const startDt = new Date(`${p.date}T${p.start}:00`);
    const endDt = new Date(startDt.getTime() + p.duration * 3600000);
    const tech = techs.find((x) => x.id === p.assigned_to);
    try {
      await api.put(`/tickets/${p.ticket.id}`, {
        assigned_to: p.assigned_to || null,
        assigned_to_name: tech?.name || null,
        scheduled_date: startDt.toISOString(),
        scheduled_end: endDt.toISOString(),
      });
      toast.success(t("saved"));
      setPanel(null);
      load();
    } catch { toast.error(t("error")); }
  };

  const Appt = ({ tk }) => {
    const tech = techs.find((x) => x.id === tk.assigned_to);
    return (
      <button
        data-testid={`appt-${tk.id}`}
        onClick={() => manage(user.role) && openPanel(tk)}
        className={`w-full text-left rounded-lg border bg-white p-2.5 shadow-sm hover:shadow-md transition-shadow ${manage(user.role) ? "cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-center justify-between gap-1">
          <span className="text-[11px] font-mono font-semibold text-slate-700 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />{fmtTime(tk.scheduled_date)}{tk.scheduled_end ? `–${fmtTime(tk.scheduled_end)}` : ""}
          </span>
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${categoryColor(tk.category)}`}>{t(tk.category)}</span>
        </div>
        <div className="mt-1 text-xs font-medium text-slate-900 line-clamp-2">{tk.title}</div>
        <div className="text-[10px] text-slate-400 line-clamp-1">{locLine(tk)}</div>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-[10px] text-slate-600 inline-flex items-center gap-1 truncate">
            <User className="w-3 h-3" />{tech?.name || tk.assigned_to_name || t("unassigned")}
          </span>
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${statusBadge(tk.status)}`}>{t(tk.status === "completed" ? "completed_s" : tk.status)}</span>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("schedule")}</h1>
        <div className="flex items-center gap-2">
          {manage(user.role) && (
            <select data-testid="tech-filter" value={techFilter} onChange={(e) => setTechFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none">
              <option value="">{t("allTechs")}</option>
              {techs.map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
            </select>
          )}
          <div className="flex items-center rounded-lg border border-slate-300 overflow-hidden">
            <button data-testid="prev-week" onClick={() => setWeekStart(addDays(weekStart, -7))} className="px-2.5 py-2 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="today-btn" onClick={() => setWeekStart(mondayOf(new Date()))} className="px-3 py-2 text-sm font-medium border-x border-slate-200 hover:bg-slate-50">{t("today")}</button>
            <button data-testid="next-week" onClick={() => setWeekStart(addDays(weekStart, 7))} className="px-2.5 py-2 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <div className="text-sm text-slate-500">
        {weekStart.toLocaleDateString(locale, { month: "short", day: "numeric" })} – {addDays(weekEnd, -1).toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {days.map((d) => {
          const isToday = ymd(d) === ymd(new Date());
          const list = dayTickets(d);
          return (
            <div key={ymd(d)} className={`rounded-xl border p-2.5 min-h-[140px] ${isToday ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-slate-50/60"}`}>
              <div className="flex items-center justify-between mb-2 px-0.5">
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{d.toLocaleDateString(locale, { weekday: "short" })}</div>
                  <div className={`text-lg font-bold leading-none ${isToday ? "text-blue-600" : "text-slate-800"}`}>{d.getDate()}</div>
                </div>
                {list.length > 0 && <span className="text-[10px] font-semibold text-slate-500 bg-white rounded-full px-1.5 py-0.5 border border-slate-200">{list.length}</span>}
              </div>
              <div className="space-y-2">
                {list.map((tk) => <Appt key={tk.id} tk={tk} />)}
              </div>
            </div>
          );
        })}
      </div>

      {scheduled.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400 text-sm">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-300" />{t("noScheduled")}
        </div>
      )}

      {user.role === "technician" && (
        <div data-testid="available-services">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">{t("availableServices")} · {available.length}</h2>
          <p className="text-xs text-slate-400 mb-3">{t("pickUpHint")}</p>
          {available.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-slate-400 text-sm">{t("noAvailableServices")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {available.map((tk) => (
                <div key={tk.id} data-testid={`available-${tk.id}`} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${categoryColor(tk.category)}`}>{t(tk.category)}</span>
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${statusBadge(tk.status)}`}>{t(tk.status === "completed" ? "completed_s" : tk.status)}</span>
                  </div>
                  <div className="mt-1.5 text-sm font-medium text-slate-900 line-clamp-1">{tk.title}</div>
                  <div className="text-[11px] text-slate-400 line-clamp-1">{locLine(tk)}</div>
                  {tk.scheduled_date && <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(tk.scheduled_date)}</div>}
                  <button data-testid={`claim-btn-${tk.id}`} onClick={() => claim(tk)} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-700">
                    <Plus className="w-3.5 h-3.5" /> {t("pickUp")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {manage(user.role) && unscheduled.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">{t("notScheduled")} · {unscheduled.length}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {unscheduled.map((tk) => (
              <div key={tk.id} data-testid={`unscheduled-${tk.id}`} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${categoryColor(tk.category)}`}>{t(tk.category)}</span>
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${statusBadge(tk.status)}`}>{t(tk.status === "completed" ? "completed_s" : tk.status)}</span>
                </div>
                <div className="mt-1.5 text-sm font-medium text-slate-900 line-clamp-1">{tk.title}</div>
                <div className="text-[11px] text-slate-400 line-clamp-1">{locLine(tk)}</div>
                <button data-testid={`schedule-btn-${tk.id}`} onClick={() => openPanel(tk)} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium px-3 py-1.5 hover:bg-slate-800">
                  <Plus className="w-3.5 h-3.5" /> {t("scheduleAppt")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {panel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setPanel(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{t("scheduleAppt")}</h2>
              <button onClick={() => setPanel(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm font-medium text-slate-900">{panel.ticket.title}</div>
                <div className="text-xs text-slate-400">{panel.ticket.number} · {locLine(panel.ticket)}</div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("assignedTo")}</label>
                <select data-testid="panel-tech" value={panel.assigned_to} onChange={(e) => setPanel({ ...panel, assigned_to: e.target.value })} className={inputCls}>
                  <option value="">{t("selectTechnician")}</option>
                  {techs.map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("scheduledDate")}</label>
                  <input data-testid="panel-date" type="date" value={panel.date} onChange={(e) => setPanel({ ...panel, date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("startTime")}</label>
                  <input data-testid="panel-start" type="time" value={panel.start} onChange={(e) => setPanel({ ...panel, start: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("durationHours")}</label>
                <input data-testid="panel-duration" type="number" step="0.5" min="0.5" value={panel.duration} onChange={(e) => setPanel({ ...panel, duration: parseFloat(e.target.value) || 0.5 })} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setPanel(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="panel-save" onClick={savePanel} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
